import { createSafeParseResponse, VestigasHTTPError } from '@vestigas/data';

import { HTTPError } from '~/services/kyClient';

import { log } from '~/utils';

// Returns a translated error message for a known HTTP status, or '' for non-HTTP errors.
export function resolveMutationErrorMessage(
  error: unknown,
  messageByStatus: Record<number, () => string>,
  fallback: () => string,
): string {
  if (!(error instanceof HTTPError)) return '';
  return (messageByStatus[error.response.status] ?? fallback)();
}

// Extracts a human-readable message from arbitrary backend error detail shapes,
// e.g. a plain string, FastAPI's `{ msg }` entries, or a nested `{ message }`.
function extractDetailMessage(detail: unknown): string {
  if (typeof detail === 'string') {
    return detail.trim();
  }

  if (Array.isArray(detail)) {
    return detail
      .map((entry) => extractDetailMessage(entry))
      .filter(Boolean)
      .join('; ');
  }

  if (detail !== null && typeof detail === 'object') {
    if ('msg' in detail && typeof detail.msg === 'string') {
      return detail.msg.trim();
    }

    if ('message' in detail && typeof detail.message === 'string') {
      return detail.message.trim();
    }
  }

  return '';
}

export function resolveVestigasHttpErrorDetail(error: unknown): string {
  if (!(error instanceof VestigasHTTPError)) return '';

  if (typeof error.parsedBody === 'string') {
    return error.parsedBody.trim();
  }

  const detail =
    error.parsedBody !== null &&
    typeof error.parsedBody === 'object' &&
    'detail' in error.parsedBody
      ? error.parsedBody.detail
      : null;

  return extractDetailMessage(detail);
}

// ESCAPE HATCH — prefer `validate: { response: schema }` on the request, which
// covers every admin call site today. This exists for schemas whose output does
// not survive the JSON round trip the ky client's body substitution needs
// (`z.coerce.date()` → `Date`); no such response schema exists yet, hence no
// consumers. Reports drift via `log.responseValidation` and returns the raw
// response on failure so drift never breaks the UI. Delete this together with
// the suppression if the first such schema never materializes.
// fallow-ignore-next-line unused-export -- deliberate, see above
export const safeParseResponse = createSafeParseResponse({ log });
